import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import LogRocket from 'logrocket';
import { AppThunk } from '../util/store';
import {
  CheckoutResource,
  CurrentCheckoutResponse,
  CheckoutSuccessResponse,
  CheckoutItems,
  AcceptDepositBumpingSuccessResponse,
  PlanApprovalCheckResponse,
  ApplicationCompletedBy,
  ApplicationStep,
  ApprovalResponse,
  PlanResponse,
  PlanStatus,
  NextStep,
} from '@payright/types-bnpl-checkout-api';
import { PaymentCalculatorData } from '@payright/web-components';
import { loading, loadingFunctionInterface } from '../util/loadingFunctionInterface';
import getConstants from '../util/constants';
import { PreApprovalCheckResponse } from '@payright/types-bnpl-checkout-api';
import { BnplCheckoutApi } from '../util/bnpl-checkout-api';

const { CONTACT_US_PHONE_NUMBER } = getConstants();

type InitialState = {
  loading: boolean;
  activeRequests: Array<checkoutRequestId>;
  paymentDetails: PaymentCalculatorData;
  paymentDetailsEdit?: boolean;
  checkout?: CheckoutSuccessResponse | null;
  checkoutItems?: CheckoutItems;
  checkoutValidationStatus: boolean;
  hasErrors: boolean;
  errorTitle?: string | null;
  errorMessage?: string | null;
  termsAndConditionAccepted: boolean;
  showAllAvailableTerms?: boolean;
};

export const initialState: InitialState = {
  loading: false,
  activeRequests: [],
  paymentDetails: {
    saleAmount: 1000,
    depositPercent: 10,
    paymentFrequency: 'Fortnightly',
    paymentPeriod: 12,
    establishmentFee: 29,
    depositAmount: 100,
  },
  paymentDetailsEdit: false,
  checkout: null,
  checkoutValidationStatus: false,
  hasErrors: false,
  errorTitle: null,
  errorMessage: null,
  termsAndConditionAccepted: false,
  showAllAvailableTerms: false,
};

type checkoutRequestId =
  | 'attachCustomer'
  | 'updateCheckout'
  | 'getCurrentCheckout'
  | 'getCurrentCheckoutItems'
  | 'getCheckoutValidation'
  | 'checkoutCancellation'
  | 'runPlanPreApproval'
  | 'acceptDepositBumping'
  | 'getPlanApproval'
  | 'getPlanApprovalCheck'
  | 'updateBsoOptions';

const startLoading: loadingFunctionInterface<checkoutRequestId> = (
  activeRequests: Array<checkoutRequestId>,
  requestId: checkoutRequestId
): loading<checkoutRequestId> => {
  activeRequests = [...activeRequests, requestId];
  return {
    activeRequests: activeRequests,
    loading: activeRequests.length > 0,
  };
};

const finishLoading: loadingFunctionInterface<checkoutRequestId> = (
  activeRequests: Array<checkoutRequestId>,
  requestId: checkoutRequestId
): loading<checkoutRequestId> => {
  activeRequests = activeRequests.filter((item) => item !== requestId);
  return {
    activeRequests: activeRequests,
    loading: activeRequests.length > 0,
  };
};

const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    resetCheckout: () => initialState,
    updateSaleDetails(
      state,
      action: PayloadAction<{
        paymentDetails: PaymentCalculatorData;
      }>
    ) {
      return {
        ...state,
        paymentDetails: action.payload.paymentDetails,
      };
    },
    attachCustomerBegin(state) {
      const loadingState = startLoading(state.activeRequests, 'attachCustomer');
      return {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
      };
    },
    attachCustomerSuccess(
      state,
      action: PayloadAction<{
        checkout: CheckoutResource;
      }>
    ) {
      const loadingState = finishLoading(state.activeRequests, 'attachCustomer');
      return {
        ...state,
        hasErrors: false,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        checkout: action.payload.checkout,
      };
    },
    attachCustomerFailure(state, action: PayloadAction<{ errorMessage: string }>) {
      const loadingState = finishLoading(state.activeRequests, 'attachCustomer');
      return {
        ...state,
        hasErrors: true,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        errorMessage: action.payload.errorMessage,
      };
    },
    updateCheckoutBegin: (state) => {
      const loadingState = startLoading(state.activeRequests, 'updateCheckout');
      return {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
      };
    },
    updateCheckoutSuccess(state) {
      const loadingState = finishLoading(state.activeRequests, 'updateCheckout');
      return {
        ...state,
        paymentDetailsEdit: true,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
      };
    },
    updateCheckoutFailure(state, action: PayloadAction<{ errorMessage: string }>) {
      const loadingState = finishLoading(state.activeRequests, 'updateCheckout');
      return {
        ...state,
        hasErrors: true,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        errorMessage: action.payload.errorMessage,
      };
    },
    getCurrentCheckoutSessionBegin(state) {
      const loadingState = startLoading(state.activeRequests, 'getCurrentCheckout');
      return {
        ...state,
        hasErrors: false,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
      };
    },
    getCurrentCheckoutSessionSuccess(state, action: PayloadAction<CurrentCheckoutResponse>) {
      const loadingState = finishLoading(state.activeRequests, 'getCurrentCheckout');
      const { payload } = action;
      return {
        ...state,
        checkout: {
          ...state.checkout,
          ...payload,
        },
        paymentDetails: payload.attributes.paymentDetails,
        showAllAvailableTerms: payload.attributes.showAllAvailableTerms,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
      };
    },
    getCurrentCheckoutSessionFailure(state, action: PayloadAction<{ errorMessage: string }>) {
      const loadingState = finishLoading(state.activeRequests, 'getCurrentCheckout');
      return {
        ...state,
        hasErrors: true,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        errorMessage: action.payload.errorMessage,
      };
    },
    getCheckoutValidationBegin(state) {
      const loadingState = startLoading(state.activeRequests, 'getCheckoutValidation');
      return {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        hasErrors: false,
        errorTitle: null,
        errorMessage: null,
      };
    },
    getCheckoutValidationSuccess(state) {
      const loadingState = finishLoading(state.activeRequests, 'getCheckoutValidation');
      return {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        hasErrors: false,
      };
    },
    getCheckoutValidationFailure(
      state,
      action: PayloadAction<{ errorTitle: string; errorMessage: string }>
    ) {
      const loadingState = finishLoading(state.activeRequests, 'getCheckoutValidation');
      return {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        hasErrors: true,
        errorTitle: action.payload.errorTitle,
        errorMessage: action.payload.errorMessage,
      };
    },
    checkoutCancellationBegin(state) {
      const loadingState = startLoading(state.activeRequests, 'checkoutCancellation');
      return {
        ...state,
        hasErrors: false,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
      };
    },
    checkoutCancellationSuccess: (state) => {
      const loadingState = finishLoading(state.activeRequests, 'checkoutCancellation');
      return {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        hasErrors: false,
      };
    },
    checkoutCancellationFailure: (
      state,
      action: PayloadAction<{ errorTitle: string; errorMessage: string }>
    ) => {
      const loadingState = finishLoading(state.activeRequests, 'checkoutCancellation');
      return {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        hasErrors: true,
        errorTitle: action.payload.errorMessage,
        errorMessage: action.payload.errorMessage,
      };
    },
    runPlanPreApprovalBegin(state) {
      const loadingState = startLoading(state.activeRequests, 'runPlanPreApproval');
      return {
        ...state,
        hasErrors: false,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
      };
    },
    runPlanPreApprovalSuccess: (state, action: PayloadAction<PreApprovalCheckResponse>) => {
      const loadingState = finishLoading(state.activeRequests, 'runPlanPreApproval');
      return {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        hasErrors: false,
      };
    },
    runPlanPreApprovalFailure: (state, action: PayloadAction<{ errorMessage: string }>) => {
      const loadingState = finishLoading(state.activeRequests, 'runPlanPreApproval');
      return {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        hasErrors: true,
        errorMessage: action.payload.errorMessage,
      };
    },
    acceptDepositBumpingBegin(state) {
      const loadingState = startLoading(state.activeRequests, 'acceptDepositBumping');
      return {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
      };
    },
    acceptDepositBumpingSuccess: (
      state,
      action: PayloadAction<AcceptDepositBumpingSuccessResponse>
    ) => {
      const loadingState = finishLoading(state.activeRequests, 'acceptDepositBumping');

      const attributes = state.checkout?.attributes
        ? {
            ...state.checkout.attributes,
            depositPayable: action.payload.depositPaid,
            depositPaidPercentage: action.payload.depositPaidPercentage,
            paymentDetails: {
              ...state.checkout.attributes.paymentDetails,
              depositAmount: action.payload.depositPaid,
              depositPercent: action.payload.depositPaidPercentage,
            },
          }
        : undefined;

      const newState = {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        hasErrors: false,
        checkout: {
          ...state.checkout,
          nextSteps: action.payload.nextSteps,
          attributes: attributes,
        },
        paymentDetails: {
          ...state.paymentDetails,
          depositAmount: action.payload.depositPaid,
          depositPercent: action.payload.depositPaidPercentage,
        },
      };
      return newState;
    },
    acceptDepositBumpingFailure: (state, action: PayloadAction<{ errorMessage: string }>) => {
      const loadingState = finishLoading(state.activeRequests, 'acceptDepositBumping');
      return {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        hasErrors: true,
        errorMessage: action.payload.errorMessage,
      };
    },
    getPlanApprovalBegin(state) {
      const loadingState = startLoading(state.activeRequests, 'getPlanApproval');
      return {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        hasErrors: false,
        errorTitle: null,
        errorMessage: null,
      };
    },
    getPlanApprovalSuccess(state) {
      const loadingState = finishLoading(state.activeRequests, 'getPlanApproval');
      return {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        hasErrors: false,
        errorTitle: null,
        errorMessage: null,
      };
    },
    getPlanApprovalFailure(
      state,
      action: PayloadAction<{ errorTitle: string; errorMessage: string }>
    ) {
      const loadingState = finishLoading(state.activeRequests, 'getPlanApproval');
      return {
        ...state,
        hasErrors: true,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        errorTitle: action.payload.errorTitle,
        errorMessage: action.payload.errorMessage,
      };
    },
    getPlanApprovalCheckBegin(state) {
      const loadingState = startLoading(state.activeRequests, 'getPlanApprovalCheck');
      return {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        hasErrors: false,
        errorTitle: null,
        errorMessage: null,
      };
    },
    getPlanApprovalCheckSuccess(state) {
      const loadingState = finishLoading(state.activeRequests, 'getPlanApprovalCheck');
      return {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
      };
    },
    getPlanApprovalCheckFailure(
      state,
      action: PayloadAction<{ errorTitle: string; errorMessage: string }>
    ) {
      const loadingState = finishLoading(state.activeRequests, 'getPlanApprovalCheck');
      return {
        ...state,
        hasErrors: true,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        errorTitle: action.payload.errorTitle,
        errorMessage: action.payload.errorMessage,
      };
    },
    updateBsoOptionsBegin: (state) => {
      const loadingState = startLoading(state.activeRequests, 'updateBsoOptions');
      return {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
      };
    },
    updateBsoOptionsSuccess: (state, action: PayloadAction<{ status: string }>) => {
      const loadingState = finishLoading(state.activeRequests, 'updateBsoOptions');
      return {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        hasErrors: false,
      };
    },
    updateBsoOptionsFailure: (state, action: PayloadAction<{ errorMessage: string }>) => {
      const loadingState = finishLoading(state.activeRequests, 'updateBsoOptions');
      return {
        ...state,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        hasErrors: true,
      };
    },
    setTermsAndConditionAccepted: (state, action: PayloadAction<{ accepted: boolean }>) => {
      return {
        ...state,
        termsAndConditionAccepted: action.payload.accepted,
      };
    },
    getCurrentCheckoutItems(state) {
      const loadingState = startLoading(state.activeRequests, 'getCurrentCheckoutItems');
      return {
        ...state,
        hasErrors: false,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
      };
    },
    getCurrentCheckoutItemsSuccess(state, action: PayloadAction<CheckoutItems>) {
      const loadingState = finishLoading(state.activeRequests, 'getCurrentCheckoutItems');
      const { payload } = action;
      return {
        ...state,
        checkout: {
          ...state.checkout,
        },
        checkoutItems: payload,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
      };
    },
    getCurrentCheckoutItemsFailure(state, action: PayloadAction<{ errorMessage: string }>) {
      const loadingState = finishLoading(state.activeRequests, 'getCurrentCheckoutItems');
      return {
        ...state,
        hasErrors: true,
        activeRequests: loadingState.activeRequests,
        loading: loadingState.loading,
        errorMessage: action.payload.errorMessage,
      };
    },
  },
});

export const { resetCheckout, updateSaleDetails, setTermsAndConditionAccepted } =
  checkoutSlice.actions;

const {
  attachCustomerBegin,
  attachCustomerSuccess,
  attachCustomerFailure,
  updateCheckoutBegin,
  updateCheckoutSuccess,
  updateCheckoutFailure,
  getCurrentCheckoutSessionBegin,
  getCurrentCheckoutSessionSuccess,
  getCurrentCheckoutSessionFailure,
  runPlanPreApprovalBegin,
  checkoutCancellationBegin,
  checkoutCancellationSuccess,
  checkoutCancellationFailure,
  getCurrentCheckoutItems,
  getCurrentCheckoutItemsSuccess,
  getCurrentCheckoutItemsFailure,
  runPlanPreApprovalSuccess,
  runPlanPreApprovalFailure,
  acceptDepositBumpingBegin,
  acceptDepositBumpingSuccess,
  acceptDepositBumpingFailure,
  getCheckoutValidationBegin,
  getCheckoutValidationSuccess,
  getCheckoutValidationFailure,
  getPlanApprovalBegin,
  getPlanApprovalSuccess,
  getPlanApprovalFailure,
  getPlanApprovalCheckBegin,
  getPlanApprovalCheckSuccess,
  getPlanApprovalCheckFailure,
  updateBsoOptionsBegin,
  updateBsoOptionsSuccess,
  updateBsoOptionsFailure,
} = checkoutSlice.actions;

export default checkoutSlice.reducer;

// -------------------------
// - Thunks - FETCHING THE CHECKOUT ATTRIBUTES TO RE-INTIALISE THE CHECKOUT STATE ON PAGE REFRESH
// -------------------------
export const fetchCheckoutAttributes =
  (checkoutId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getCurrentCheckoutSessionBegin());
      const bnplCheckoutApi = new BnplCheckoutApi();
      const currentCheckout: CurrentCheckoutResponse = await bnplCheckoutApi.getCurrentCheckout(
        checkoutId
      );
      dispatch(getCurrentCheckoutSessionSuccess(currentCheckout));
    } catch (error) {
      dispatch(getCurrentCheckoutSessionFailure(error.message));
    }
  };

// -------------------------
// - Thunks - FETCHING THE CHECKOUT ATTRIBUTES TO RE-INTIALISE THE CHECKOUT STATE ON PAGE REFRESH
// -------------------------
export const fetchCheckoutItems =
  (checkoutId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getCurrentCheckoutItems());
      const bnplCheckoutApi = new BnplCheckoutApi();
      const checkoutItems: CheckoutItems = await bnplCheckoutApi.getCurrentCheckoutItems(
        checkoutId
      );
      dispatch(getCurrentCheckoutItemsSuccess(checkoutItems));
    } catch (error) {
      dispatch(getCurrentCheckoutItemsFailure(error.message));
    }
  };

// -------------------------
// - Thunks - UPDATE CHECKOUT
// -------------------------
/**
 * TODO:
 * Rewrite how this works so it can take as many or as few props
 * as it needs
 * We don't need to force paymentDetails or half the checkoutData
 */
export const updateCheckout =
  ({
    checkoutIdentifier,
    paymentDetails,
    checkoutData,
  }: {
    checkoutData: any;
    paymentDetails: PaymentCalculatorData;
    checkoutIdentifier: string;
  }): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      dispatch(updateCheckoutBegin());

      const updateCheckoutParams = {
        attributes: {
          saleAmount: paymentDetails.saleAmount,
          terms: paymentDetails.paymentFrequency,
          depositPayable: paymentDetails.depositAmount,
          depositPaidPercentage: paymentDetails.depositPercent,
          paymentPeriod: paymentDetails.paymentPeriod,
          paymentDetails: paymentDetails,
          type: checkoutData.type,
          redirectUrl: checkoutData.redirectUrl,
          expiresAt: checkoutData.expiresAt,
          step: checkoutData.step,
        },
      };

      const bnplCheckoutApi = new BnplCheckoutApi();
      await bnplCheckoutApi.updateCheckout(checkoutIdentifier, updateCheckoutParams);

      dispatch(updateSaleDetails({ paymentDetails: paymentDetails }));
      dispatch(updateCheckoutSuccess());
      dispatch(fetchCheckoutAttributes(checkoutIdentifier));
      return true;
    } catch (err) {
      dispatch(updateCheckoutFailure({ errorMessage: 'Update checkout failed' }));
      throw err.message;
    }
  };

// -------------------------
// - Thunks - ATTACH CUSTOMER ID TO A CHECKOUT
// -------------------------
export const attachCustomer =
  (
    checkoutId: string,
    customerId: string,
    applicationCompletedBy: ApplicationCompletedBy
  ): AppThunk<Promise<string | null>> =>
  async (dispatch, getState) => {
    const checkout = getState().checkout?.checkout || null;

    if (checkout?.planId !== null && checkout?.customerId !== null) {
      // console.log('Skip attaching');
      return checkout?.planId as string;
    }

    dispatch(attachCustomerBegin());
    LogRocket.track('Attaching Customer');

    try {
      const bnplCheckoutApi = new BnplCheckoutApi();
      const result = await bnplCheckoutApi.customerToCheckout(
        customerId,
        checkoutId,
        applicationCompletedBy
      );
      dispatch(attachCustomerSuccess({ checkout: result }));
      LogRocket.track('Attach Customer Successful');

      // console.log('Attached: ', result);
      return result.planId;
    } catch (err) {
      dispatch(attachCustomerFailure({ errorMessage: err.message }));
      console.log(err);
      LogRocket.track(`Attaching Customer Failed - Error: ${err.message}`);
      throw new Error(err);
    }
  };

// -------------------------
// - Thunks - PLAN PRE-APPROVAL CHECK
// -------------------------
export const runPlanPreApprovalCheck =
  ({
    checkoutId,
    isCheckoutUpdated,
    isExistingCustomer,
    onSuccessHandleRedirection,
  }: {
    checkoutId: string;
    isCheckoutUpdated: boolean;
    isExistingCustomer: boolean;
    onSuccessHandleRedirection: (response: PreApprovalCheckResponse) => void;
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(runPlanPreApprovalBegin());
      const bnplCheckoutApi = new BnplCheckoutApi();
      const response = await bnplCheckoutApi.runPlanPreApprovalCheck(
        checkoutId,
        isCheckoutUpdated,
        isExistingCustomer
      );

      // We run this before redirecting so we update the state before getState()
      dispatch(runPlanPreApprovalSuccess(response));

      onSuccessHandleRedirection(response);
    } catch (error) {
      console.log(error.message);
      dispatch(runPlanPreApprovalFailure(error.message));
    }
  };

// -------------------------
// - Thunks - ACCEPT DEPOSIT BUMP AND PLAN PRE-APPROVAL
// -------------------------
export const acceptDepositBumping =
  ({
    checkoutId,
    depositAmount,
    depositPercentage,
    onSuccessHandleRedirection,
  }: {
    checkoutId: string;
    depositAmount: number;
    depositPercentage: number;
    onSuccessHandleRedirection: (params: {
      nextSteps: Array<NextStep>;
      paymentDetails?: PaymentCalculatorData;
    }) => void;
  }): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    try {
      dispatch(acceptDepositBumpingBegin());
      const bnplCheckoutApi = new BnplCheckoutApi();

      const response = await bnplCheckoutApi.runAcceptPreApprovalCheck(
        checkoutId,
        depositAmount,
        depositPercentage
      );

      // We run this before redirecting so we update the state before getState()
      dispatch(acceptDepositBumpingSuccess(response));

      const paymentDetails = { ...getState().checkout.paymentDetails };
      const params = {
        nextSteps: response.nextSteps,
        paymentDetails,
      };

      onSuccessHandleRedirection(params);
    } catch (error) {
      dispatch(acceptDepositBumpingFailure(error.message));
    }
  };

// -------------------------
// - Thunks - UPDATE BANK STATEMENT OPTIONS
// -------------------------
export const updateBsoOptions =
  ({
    checkoutId,
    provideBso,
    onSuccessHandleRedirection,
  }: {
    checkoutId: string;
    provideBso: string;
    onSuccessHandleRedirection: (params: {
      nextSteps: Array<NextStep>;
      paymentDetails?: PaymentCalculatorData;
    }) => void;
  }): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(updateBsoOptionsBegin());

      const bnplCheckoutApi = new BnplCheckoutApi();
      const response = await bnplCheckoutApi.updateBsoOptions(checkoutId, provideBso);

      const paymentDetails = { ...getState().checkout.paymentDetails };
      const params = {
        nextSteps: response.nextSteps,
        paymentDetails,
      };

      onSuccessHandleRedirection(params);

      dispatch(updateBsoOptionsSuccess(response));
    } catch (error) {
      dispatch(updateBsoOptionsFailure(error.message));
    }
  };

// ------------------------------
// - Thunks - Validate code
// ------------------------------
export const checkoutValidate =
  (planId: string, checkoutId: string, validationCode?: string): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      if (!validationCode) {
        getCheckoutValidationFailure({
          errorTitle: 'No validation code provided',
          errorMessage: 'Validation code is required',
        });
        return false;
      }

      const bnplCheckoutApi = new BnplCheckoutApi();
      dispatch(getCheckoutValidationBegin());
      LogRocket.track('Verifying plan validation code');

      const currentCheckoutVerification = await bnplCheckoutApi.verifyPlanSubmission(
        planId,
        checkoutId,
        validationCode
      );

      if (currentCheckoutVerification.status === true) {
        dispatch(getCheckoutValidationSuccess());
        return true;
      } else {
        dispatch(
          getCheckoutValidationFailure({
            errorTitle: 'Invalid validation code',
            errorMessage: 'Recheck the validation code',
          })
        );
        return false;
      }
    } catch (error) {
      dispatch(
        getPlanApprovalFailure({
          errorTitle: 'Unexpected error',
          errorMessage: `There was an unexpected error. Please try again or contact our office for further assistance
        on ${CONTACT_US_PHONE_NUMBER}.`,
        })
      );
      return false;
    }
  };

// ------------------------------
// - Thunks - Perform dry-run
// ------------------------------
export const checkoutCheckApprovalOutcome =
  (checkoutId: string): AppThunk<Promise<PlanApprovalCheckResponse | void>> =>
  async (dispatch) => {
    try {
      const bnplCheckoutApi = new BnplCheckoutApi();

      dispatch(getPlanApprovalCheckBegin());

      try {
        const planApproval = await bnplCheckoutApi.planApprovalCheck(checkoutId);
        dispatch(getPlanApprovalCheckSuccess());
        LogRocket.track('Plan approval (check) complete with outcome ' + planApproval.outcome);
        return planApproval;
      } catch (error) {
        LogRocket.track('Plan approval (check) HTTP response error');

        // Try, try, try again
        const getOutcome = () => bnplCheckoutApi.planApprovalCheck(checkoutId);

        const retryMultipleTimes = (
          getOutcome: () => Promise<PlanApprovalCheckResponse>,
          retries = 6,
          delay = 5000
        ) =>
          new Promise<PlanApprovalCheckResponse>((resolve, reject) => {
            getOutcome()
              .then((result) => {
                resolve(result);
                return;
              })
              .catch((error) => {
                setTimeout(() => {
                  if (retries === 0) {
                    reject(error);
                    return;
                  }

                  // Retry
                  retryMultipleTimes(getOutcome, retries - 1, delay).then(resolve, reject);
                }, delay);
              });
          });

        return retryMultipleTimes(getOutcome)
          .then((result) => {
            dispatch(getPlanApprovalCheckSuccess());
            LogRocket.track(
              'Plan approval (check) complete after retry with status ' + result.outcome
            );
            return result;
          })
          .catch(() => {
            LogRocket.track('Plan approval (check) did not complete after numerous retries');
            dispatch(
              getPlanApprovalCheckFailure({
                errorTitle: 'Unexpected error',
                errorMessage: `There was an unexpected error. Please try again or contact our office for further assistance
              on ${CONTACT_US_PHONE_NUMBER}.`,
              })
            );
          });
      }
    } catch (error) {
      dispatch(
        getPlanApprovalCheckFailure({
          errorTitle: 'Unexpected error',
          errorMessage: `There was an unexpected error. Please try again or contact our office for further assistance
        on ${CONTACT_US_PHONE_NUMBER}.`,
        })
      );
    }
  };

// -------------------------
// - Thunks - PLAN APPROVAL
// -------------------------
export const checkoutApproval =
  (
    planId: string,
    checkoutId: string
  ): AppThunk<Promise<{ result: PlanStatus; nextStep: ApplicationStep } | void>> =>
  async (dispatch) => {
    try {
      const bnplCheckoutApi = new BnplCheckoutApi();

      dispatch(getPlanApprovalBegin());

      try {
        const planApproval = await bnplCheckoutApi.planApproval(checkoutId);
        dispatch(getPlanApprovalSuccess());
        LogRocket.track('Plan approval complete with status ' + planApproval.result);
        return { result: planApproval.result, nextStep: planApproval.nextStep };
      } catch (error) {
        LogRocket.track('Plan approval HTTP response error');
        // If planApproval fails due to timeout error or 409 error, poll the server to retrieve the latest plan status until
        // it has changed to something other than "Pending". Then run the usual flow

        type PartialPlanResponse = Pick<PlanResponse, 'status' | 'applicationStep'>;

        const getPartialPlan: () => Promise<PartialPlanResponse> = async () => {
          const response = await bnplCheckoutApi.getPlan(checkoutId, planId);
          return {
            status: response.status,
            applicationStep: response.applicationStep,
          };
        };

        const retryMultipleTimes = (
          getPartialPlan: () => Promise<PartialPlanResponse>,
          retries = 6,
          delay = 5000
        ) => {
          return new Promise<ApprovalResponse>((resolve, reject) => {
            getPartialPlan()
              .then((response) => {
                if (response.status !== 'Pending') {
                  resolve({
                    result: response.status,
                    nextStep: response.applicationStep,
                  });
                  return;
                } else {
                  throw new Error('Plan status is still in Pending state');
                }
              })
              .catch((error) => {
                setTimeout(() => {
                  if (retries === 0) {
                    reject(error);
                    return;
                  }

                  // Retry
                  retryMultipleTimes(getPartialPlan, retries - 1, delay).then(resolve, reject);
                }, delay);
              });
          });
        };

        return retryMultipleTimes(getPartialPlan)
          .then((result) => {
            dispatch(getPlanApprovalSuccess());
            LogRocket.track('Plan approval complete after retry with status ' + result.result);
            return result;
          })
          .catch(() => {
            LogRocket.track('Plan approval did not complete after numerous retries');
            dispatch(
              getPlanApprovalFailure({
                errorTitle: 'Unexpected error',
                errorMessage: `There was an unexpected error. Please try again or contact our office for further assistance
              on ${CONTACT_US_PHONE_NUMBER}.`,
              })
            );
            return;
          });
      }
    } catch (error) {
      dispatch(
        getPlanApprovalFailure({
          errorTitle: 'Unexpected error',
          errorMessage: `There was an unexpected error. Please try again or contact our office for further assistance
        on ${CONTACT_US_PHONE_NUMBER}.`,
        })
      );
      return;
    }
  };

// -------------------------
// - Thunks - PLAN CANCELLATION
// -------------------------
export const checkoutCancellation =
  (checkoutId: string): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      dispatch(checkoutCancellationBegin());
      const bnplCheckoutApi = new BnplCheckoutApi();
      // Initiate plan cancellation, with given checkout id'
      await bnplCheckoutApi.planCancellation(checkoutId);

      // Clear all local storage upon plan cancellation
      localStorage.removeItem('authToken');
      localStorage.removeItem('customerId');

      dispatch(checkoutCancellationSuccess());

      return true;
    } catch (error) {
      dispatch(
        checkoutCancellationFailure({
          errorTitle: 'Unexpected error',
          errorMessage: `There was an unexpected error. Please try again or contact our office for further assistance
        on ${CONTACT_US_PHONE_NUMBER}.`,
        })
      );
    }

    return false;
  };
